import { supabase } from './supabase';
import { redirect } from 'react-router-dom';

export type UserRole = 'distributor' | 'reseller' | 'customer';

export interface User {
  id: string;
  email: string;
  role: UserRole;
  firstName?: string | null;
  lastName?: string | null;
}

export async function signIn(email: string, password: string): Promise<User | null> {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    console.error('Authentication error:', error.message);
    return null;
  }

  if (!data.user) {
    return null;
  }

  // Get user data from users table
  const { data: userData, error: userError } = await supabase
    .from('users')
    .select('role, first_name, last_name')
    .eq('user_id', data.user.id)
    .single();

  if (userError || !userData) {
    console.error('Failed to fetch user data:', userError?.message);
    return null;
  }

  return {
    id: data.user.id,
    email: data.user.email || '',
    role: userData.role as UserRole,
    firstName: userData.first_name,
    lastName: userData.last_name
  };
}

export async function signOut() {
  await supabase.auth.signOut();
  redirect('/login');
}

export async function getCurrentUser(): Promise<User | null> {
  const { data } = await supabase.auth.getSession();
  
  if (!data.session?.user) {
    return null;
  }

  // Get user data from users table
  const { data: userData, error: userError } = await supabase
    .from('users')
    .select('role, first_name, last_name')
    .eq('user_id', data.session.user.id)
    .single();

  if (userError || !userData) {
    return null;
  }

  return {
    id: data.session.user.id,
    email: data.session.user.email || '',
    role: userData.role as UserRole,
    firstName: userData.first_name,
    lastName: userData.last_name
  };
}

// Helper function to get additional reseller or customer info based on role
export async function getUserContextData(user: User): Promise<any> {
  if (!user) return null;
  
  if (user.role === 'reseller') {
    // Get reseller data
    const { data: resellerData } = await supabase
      .from('reseller_users')
      .select('reseller_id')
      .eq('user_id', user.id)
      .single();
    
    if (resellerData?.reseller_id) {
      const { data: reseller } = await supabase
        .from('resellers')
        .select('*')
        .eq('reseller_id', resellerData.reseller_id)
        .single();
      
      return { ...user, reseller };
    }
  } 
  else if (user.role === 'customer') {
    // Get customer data
    const { data: customerData } = await supabase
      .from('customer_users')
      .select('customer_id')
      .eq('user_id', user.id)
      .single();
    
    if (customerData?.customer_id) {
      const { data: customer } = await supabase
        .from('customers')
        .select('*, reseller:resellers(*)')
        .eq('customer_id', customerData.customer_id)
        .single();
      
      return { ...user, customer };
    }
  }
  
  return user;
}